import { Link, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingBag,
  Users,
  FileText,
  Megaphone,
  Search,
  BarChart3,
  Settings,
  Bell,
  Sun,
  Moon,
  Menu,
  LogOut,
  BookOpen,
  Star,
  Linkedin,
} from "lucide-react";
import { useState, useEffect } from "react";
import logo from "@/assets/logo.svg";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";

const nav = [
  { to: "/", label: "Overview", icon: LayoutDashboard },
  { to: "/products", label: "Products", icon: Package },
  { to: "/orders", label: "Orders", icon: ShoppingBag },
  { to: "/reviews", label: "Reviews", icon: Star },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/blog", label: "Blog", icon: FileText },
  { to: "/marketing", label: "Marketing", icon: Megaphone },

  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/guide", label: "User Guide", icon: BookOpen },
] as const;

export function AdminShell() {
  const { user, logout } = useAuth();
  const path = useLocation().pathname;
  const [open, setOpen] = useState(false);

  const [theme, setTheme] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("admin-theme") || "dark";
    }
    return "dark";
  });

  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
    } else {
      root.classList.remove("dark");
    }
    localStorage.setItem("admin-theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <div className="bg-brand-canvas min-h-screen text-foreground transition-colors duration-300">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-72 transform border-r border-gold bg-sidebar transition-transform lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="ornate-corners flex h-full flex-col">
          <div className="flex flex-col items-center gap-3 px-6 pt-8 pb-6">
            <div className="rounded-full border border-gold p-1">
              <img src={logo} alt="Subaashghor" className="h-16 w-16 rounded-full object-contain" />
            </div>
            <div className="text-center">
              <div className="font-display text-2xl text-gold">সুবাসঘর</div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-soft">
                Subaashghor
              </div>
              <div className="mt-1 text-[10px] tracking-widest uppercase text-muted-foreground">
                Admin Console
              </div>
            </div>
          </div>

          <div className="ornate-divider mx-6" />

          <nav className="flex-1 space-y-1 overflow-y-auto px-4 py-6">
            {nav.map((item) => {
              const active = item.to === "/" ? path === "/" : path.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setOpen(false)}
                  className={cn(
                    "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-all",
                    active
                      ? "bg-sidebar-accent text-gold border border-gold/40 shadow-xs font-semibold"
                      : "text-foreground/80 hover:bg-sidebar-accent/60 hover:text-gold",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-4 w-4",
                      active ? "text-gold" : "text-foreground/60 group-hover:text-gold",
                    )}
                  />
                  <span className="tracking-wide">{item.label}</span>
                  {active && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-gold" />}
                </Link>
              );
            })}
          </nav>

          <div className="ornate-divider mx-6" />

          <div className="px-6 py-5">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sidebar-accent text-gold font-display text-lg font-bold border border-gold/30">
                  {user?.name ? user.name.charAt(0).toUpperCase() : "A"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">
                    {user?.email || "Administrator"}
                  </div>
                  <div className="truncate text-[10px] uppercase tracking-widest text-gold-soft font-semibold">
                    Super Admin
                  </div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="rounded-md border border-gold/20 p-2 text-gold-soft hover:bg-sidebar-accent hover:text-gold transition-colors"
                title="Log Out"
                aria-label="Log Out"
              >
                <LogOut className="h-4 w-4" />
              </button>
            </div>
            {/* Developer Info */}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
              <span>Developer:</span>
              <a
                href="https://www.linkedin.com/in/shanjid-ahmad-b77b5427b/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-0.5 font-semibold text-gold-soft hover:text-gold hover:underline transition-colors"
              >
                levi9111 <Linkedin className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="lg:pl-72">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-gold/20 bg-background/85 px-4 backdrop-blur lg:px-8 transition-colors duration-300">
          <button
            onClick={() => setOpen((o) => !o)}
            className="rounded-md border border-gold/30 p-2 text-gold lg:hidden"
            aria-label="Menu"
          >
            <Menu className="h-4 w-4" />
          </button>

          <div className="hidden md:block">
            <div className="font-display text-lg text-gold leading-none">
              Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
            </div>
            <div className="text-xs text-muted-foreground">A house of pure fragrance</div>
          </div>

          <div className="ml-auto flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 rounded-md border border-gold/20 bg-card/60 px-3 py-1.5 text-sm text-muted-foreground">
              <Search className="h-3.5 w-3.5" />
              <input
                placeholder="Search products, orders, customers…"
                className="w-64 bg-transparent outline-none placeholder:text-muted-foreground/70 text-foreground"
              />
            </div>
            <button
              className="rounded-md border border-gold/20 p-2 text-gold-soft hover:text-gold transition-colors"
              aria-label="Notifications"
            >
              <Bell className="h-4 w-4" />
            </button>
            <button
              onClick={toggleTheme}
              className="rounded-md border border-gold/20 p-2 text-gold-soft hover:text-gold transition-all duration-300 hover:scale-105"
              aria-label="Toggle theme"
              title={`Switch to ${theme === "dark" ? "Light" : "Dark"} Mode`}
            >
              {theme === "dark" ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4 text-primary" />}
            </button>
          </div>
        </header>

        <main className="px-4 py-8 lg:px-10">
          <Outlet />
        </main>

        <footer className="border-t border-gold/15 px-6 py-6 text-center text-xs text-muted-foreground">
          <div className="ornate-divider mx-auto mb-3 max-w-xs" />
          সুবাসঘর — A House of Pure Fragrance · © 2026
        </footer>
      </div>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}
    </div>
  );
}

/* Reusable section header */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.4em] text-gold-soft font-semibold">
          Subaashghor Admin
        </div>
        <h1 className="font-display text-3xl text-gold sm:text-4xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground max-w-xl">{subtitle}</p>}
        <div className="ornate-divider mt-3 max-w-[200px]" />
      </div>
      {action}
    </div>
  );
}

export function Panel({
  children,
  className,
  title,
  description,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
  description?: string;
}) {
  return (
    <div
      className={cn(
        "ornate-corners gold-shadow rounded-xl border border-gold/20 bg-brand-card p-6 transition-colors duration-300",
        className,
      )}
    >
      {title && (
        <div className="mb-5">
          <h3 className="font-display text-xl text-gold">{title}</h3>
          {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
        </div>
      )}
      {children}
    </div>
  );
}
